import { stopWaitForOwner } from '../authentication/actions';
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE, ENDPOINT_MESSAGE_RECEIVED, NON_PARTICIPANT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import { addforcemuteRemoteParticipant } from '../base/conference/actions';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import { SET_AUDIO_MUTED, SET_VIDEO_MUTED } from '../base/media/actionTypes';
import { setAudioMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE } from '../base/media/constants';
import { setIsSwitchingBreakoutRoom, setIsSwitchingToMainRoom } from '../base/mtApi';
import { PARTICIPANT_JOINED } from '../base/participants/actionTypes';
import { participantUpdated } from '../base/participants/actions';
import { getLocalParticipant, getParticipantById, isLocalParticipantModerator, isLocalParticipantPresenter, isParticipantModerator } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../notifications';
import { muteLocal } from '../video-menu/actions.any';
import { iAmVisitor } from '../visitors/functions';
import { announceAudienceLayout, goBackstage, goLive, joinAdmittedRoom, rejoinAfterFailedSwitch, requestAudienceLayout, requestBackstageRoster, requestMainRoomRoster, setAudienceLayout, setBackstageRoster, setJoinHidden, setLocalRecordingAllowedState, setMediaBlockAllState, setMainRoomRoster, setRoomSwitchTarget } from './actions';
import { BACKSTAGE_HELD_ERROR, BACKSTAGE_ROOM_SUFFIX, BREAKOUT_HELD_ERROR, LOCAL_RECORDING_METADATA_KEY, MEDIA_BLOCK_ALL_METADATA_KEY, WEBINAR_AUDIENCE_LAYOUT, WEBINAR_AUDIENCE_LAYOUT_METADATA_KEY, WEBINAR_AUDIENCE_LAYOUT_REQUEST, WEBINAR_BACKSTAGE_ROSTER, WEBINAR_HIDDEN_REQUEST_PROPERTY, WEBINAR_MAIN_ROSTER, WEBINAR_MOVE_REQUEST, WEBINAR_MUTE_REQUEST } from './constants';
import { amIInBackstage, amIWebinarModerator, getJoinHiddenAudience, getMainRoomName, isBackstageModeEnabled, isBackstageRoomName } from './functions';
import logger from './logger';
/**
 * Conference failures that mean "the join was refused", i.e. the participant is
 * not in the room they were switching to and never will be without help. The
 * backstage door gate surfaces as NOT_ALLOWED_ERROR; a room that jicofo has not
 * created yet surfaces as CONNECTION_ERROR ('room-does-not-exist'); a jicofo
 * that will not create the room for an unauthenticated user surfaces as
 * AUTHENTICATION_REQUIRED, whose normal handling is a five-second rejoin loop
 * that would leave the participant on "Waiting for the host" forever.
 */
const RECOVERABLE_JOIN_ERRORS = new Set([
    'conference.authenticationRequired',
    'conference.connectionError',
    'conference.connectionError.notAllowed',
    'conference.connectionError.membersOnly',
    'conference.connectionError.accessDenied'
]);
let _metadataConference;
let _metadataUpdatedHandler;
let _visitorsConference;
let _visitorsMessageHandler;
let _wasModeratorInPreviousRoom = false;
/**
 * Handles trusted webinar move requests relayed by Prosody.
 */
MiddlewareRegistry.register((store) => next => action => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    // A presenter is share-only: swallow any attempt to unmute the microphone or
    // camera before it reaches the media/track layers. The server grants the role;
    // this is the client-side enforcement of its "no mic, no camera" contract.
    if ((action.type === SET_AUDIO_MUTED || action.type === SET_VIDEO_MUTED)
        && !action.muted
        && isLocalParticipantPresenter(store.getState())) {
        store.dispatch(showNotification({
            titleKey: 'webinar.presenterMediaBlockedTitle',
            descriptionKey: 'webinar.presenterMediaBlockedDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        return action;
    }
    const result = next(action);
    switch (action.type) {
        case ENDPOINT_MESSAGE_RECEIVED:
        case NON_PARTICIPANT_MESSAGE_RECEIVED: {
            const data = action.type === ENDPOINT_MESSAGE_RECEIVED ? action.data : action.json;
            const participant = action.type === ENDPOINT_MESSAGE_RECEIVED ? action.participant : undefined;
            const { dispatch, getState } = store;
            const senderId = (_c = (_b = (_a = participant === null || participant === void 0 ? void 0 : participant.getId) === null || _a === void 0 ? void 0 : _a.call(participant)) !== null && _b !== void 0 ? _b : participant === null || participant === void 0 ? void 0 : participant._id) !== null && _c !== void 0 ? _c : action.id;
            const sender = senderId && getParticipantById(getState(), senderId);
            const senderIsModerator = ((_d = participant === null || participant === void 0 ? void 0 : participant.isModerator) === null || _d === void 0 ? void 0 : _d.call(participant))
                || isParticipantModerator(sender)
                || (data === null || data === void 0 ? void 0 : data.webinarModerator) === true;
            if (_handleMoveRequest(store, data, senderIsModerator, senderId)) {
                break;
            }
            // The backstage roster only ever comes from the server, which stamps
            // webinarModerator; a peer cannot forge it into the store.
            if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_BACKSTAGE_ROSTER) {
                if (data.webinarModerator === true && isLocalParticipantModerator(getState())) {
                    dispatch(setBackstageRoster(Array.isArray(data.occupants) ? data.occupants : []));
                }
                else {
                    logger.warn(`Ignoring backstage roster from ${senderId}`);
                }
                break;
            }
            // The mirror, for a moderator who is backstage. amIWebinarModerator
            // rather than the MUC role: backstage understates it for anonymous
            // moderators, and the server already refused to send this to anyone
            // it does not consider a moderator.
            if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_MAIN_ROSTER) {
                if (data.webinarModerator === true && amIWebinarModerator(getState())) {
                    dispatch(setMainRoomRoster(Array.isArray(data.occupants) ? data.occupants : [], data.policy));
                }
                else {
                    logger.warn(`Ignoring main-room roster from ${senderId}`);
                }
                break;
            }
            if (_handleMuteRequest(store, data, senderIsModerator, senderId)) {
                break;
            }
            if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_AUDIENCE_LAYOUT) {
                const roomName = getMainRoomName(getState());
                // Visitors receive this as a non-participant json-message whose
                // {@code from} is the moderator's real JID, not their occupant nick,
                // so senderIsModerator is often false unless Prosody stamped it.
                const trusted = senderIsModerator
                    || (iAmVisitor(getState()) && action.type === NON_PARTICIPANT_MESSAGE_RECEIVED);
                if (!trusted || !roomName) {
                    logger.warn(`Ignoring webinar audience layout from ${senderId}`);
                    break;
                }
                dispatch(setAudienceLayout(roomName, {
                    showOnlyPanelists: Boolean(data.showOnlyPanelists),
                    stageOnlyMode: Boolean(data.stageOnlyMode)
                }));
                break;
            }
            if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_AUDIENCE_LAYOUT_REQUEST
                && isLocalParticipantModerator(getState())) {
                dispatch(announceAudienceLayout());
            }
            break;
        }
        case CONFERENCE_FAILED: {
            // A refused room switch (e.g. the backstage door gate, or one of the
            // server's holds) leaves the user stranded on the room-switch screen
            // forever: those flags are only cleared on CONFERENCE_JOINED, which
            // never comes. Clear them here, say why, and put them back in the room
            // they came from.
            const { roomSwitchTarget, roomSwitchFrom } = (_e = store.getState()['features/webinar']) !== null && _e !== void 0 ? _e : {};
            const errorName = (_f = action.error) === null || _f === void 0 ? void 0 : _f.name;
            // Prosody explains a refused join in the error text; lib-meet-hour
            // passes it on as the error's only parameter (token users only -- an
            // anonymous guest's refusal reaches us as a bare connectionError).
            const errorText = typeof ((_h = (_g = action.error) === null || _g === void 0 ? void 0 : _g.params) === null || _h === void 0 ? void 0 : _h[0]) === 'string' ? action.error.params[0] : '';
            const heldBackstage = errorText === BACKSTAGE_HELD_ERROR;
            const heldBreakout = errorText.startsWith(BREAKOUT_HELD_ERROR);
            // Only a refused JOIN is recoverable here. Reacting to every failure
            // would yank people out of a room they are legitimately in over an
            // unrelated hiccup, which is worse than the hang this guards against.
            if (roomSwitchTarget && RECOVERABLE_JOIN_ERRORS.has(errorName)) {
                store.dispatch(setRoomSwitchTarget(undefined));
                store.dispatch(setIsSwitchingBreakoutRoom(false));
                store.dispatch(setIsSwitchingToMainRoom(false));
                // AUTHENTICATION_REQUIRED also arms the authentication feature's
                // cyclic "wait for the host" rejoin. It targets the room we are
                // abandoning, so cancel it before going back.
                store.dispatch(stopWaitForOwner());
                store.dispatch(showNotification(_describeRefusedSwitch(roomSwitchTarget, heldBackstage, heldBreakout), NOTIFICATION_TIMEOUT_TYPE.LONG));
                store.dispatch(rejoinAfterFailedSwitch(roomSwitchFrom));
                break;
            }
            // Not a switch: a held guest coming back to the meeting (a token user
            // reloading the page) is refused the main room the moment they join.
            // Put them where the moderator left them -- the server admits them
            // there -- instead of leaving them on an error screen.
            if (!roomSwitchTarget && errorName === 'conference.connectionError.notAllowed'
                && (heldBackstage || heldBreakout)) {
                _rejoinHeldRoom(store, action.conference, heldBackstage, errorText);
            }
            break;
        }
        case CONFERENCE_JOINED: {
            store.dispatch(setRoomSwitchTarget(undefined));
            // A roster describes the OTHER room, so neither survives a switch.
            store.dispatch(setBackstageRoster([]));
            store.dispatch(setMainRoomRoster([]));
            if (iAmVisitor(store.getState())) {
                store.dispatch(requestAudienceLayout());
            }
            else if (amIInBackstage(store.getState())) {
                store.dispatch(requestMainRoomRoster());
            }
            else if (isLocalParticipantModerator(store.getState())) {
                store.dispatch(announceAudienceLayout());
                if (isBackstageModeEnabled(store.getState())) {
                    store.dispatch(requestBackstageRoster());
                }
            }
            // Settle a prejoin "join hidden" choice.
            //
            // The server applied it before the first presence, but a client is not
            // told its own participant properties — the flag reaches everyone else
            // and never comes back to its owner. Without this the Room controls
            // switch reads OFF while the moderator is in fact hidden, and un-hiding
            // would take two flips.
            //
            // Clearing the choice afterwards makes it the one-shot it is meant to
            // be: from here on the switch owns the state, and the server's durable
            // store re-applies hidden on a rejoin, so leaving the request armed
            // would let the next room switch quietly undo an un-hide.
            _applyPrejoinJoinHidden(store, action.conference);
            // Track the moderator-set local-recording policy from room metadata,
            // and settle the initial state from the snapshot present at join time.
            const { conference } = action;
            if (_metadataConference && _metadataUpdatedHandler) {
                _metadataConference.off(MHConferenceEvents.METADATA_UPDATED, _metadataUpdatedHandler);
            }
            if (_visitorsConference && _visitorsMessageHandler) {
                _visitorsConference.off(MHConferenceEvents.VISITORS_MESSAGE, _visitorsMessageHandler);
            }
            _metadataConference = conference;
            _metadataUpdatedHandler = (metadata) => {
                _handleLocalRecordingMetadata(store.dispatch, metadata);
                _handleMediaBlockAllMetadata(store.dispatch, metadata);
                _handleAudienceLayoutMetadata(store.dispatch, store.getState, metadata);
            };
            conference.on(MHConferenceEvents.METADATA_UPDATED, _metadataUpdatedHandler);
            _visitorsConference = conference;
            _visitorsMessageHandler = (data) => {
                if (_handleMoveRequest(store, data, (data === null || data === void 0 ? void 0 : data.webinarModerator) === true, 'visitors-component')) {
                    return;
                }
                if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_AUDIENCE_LAYOUT && (data === null || data === void 0 ? void 0 : data.webinarModerator) === true) {
                    const roomName = getMainRoomName(store.getState());
                    if (roomName) {
                        store.dispatch(setAudienceLayout(roomName, {
                            showOnlyPanelists: Boolean(data.showOnlyPanelists),
                            stageOnlyMode: Boolean(data.stageOnlyMode)
                        }));
                    }
                    return;
                }
                if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_BACKSTAGE_ROSTER && (data === null || data === void 0 ? void 0 : data.webinarModerator) === true
                    && isLocalParticipantModerator(store.getState())) {
                    store.dispatch(setBackstageRoster(Array.isArray(data.occupants) ? data.occupants : []));
                    return;
                }
                if ((data === null || data === void 0 ? void 0 : data.name) === WEBINAR_MAIN_ROSTER && (data === null || data === void 0 ? void 0 : data.webinarModerator) === true
                    && amIWebinarModerator(store.getState())) {
                    store.dispatch(setMainRoomRoster(Array.isArray(data.occupants) ? data.occupants : [], data.policy));
                    return;
                }
                _handleMuteRequest(store, data, (data === null || data === void 0 ? void 0 : data.webinarModerator) === true, 'visitors-component');
            };
            conference.on(MHConferenceEvents.VISITORS_MESSAGE, _visitorsMessageHandler);
            _handleLocalRecordingMetadata(store.dispatch, (_l = (_k = (_j = conference.getMetadataHandler) === null || _j === void 0 ? void 0 : _j.call(conference)) === null || _k === void 0 ? void 0 : _k.getMetadata) === null || _l === void 0 ? void 0 : _l.call(_k));
            _handleMediaBlockAllMetadata(store.dispatch, (_p = (_o = (_m = conference.getMetadataHandler) === null || _m === void 0 ? void 0 : _m.call(conference)) === null || _o === void 0 ? void 0 : _o.getMetadata) === null || _p === void 0 ? void 0 : _p.call(_o));
            _handleAudienceLayoutMetadata(store.dispatch, store.getState, (_s = (_r = (_q = conference.getMetadataHandler) === null || _q === void 0 ? void 0 : _q.call(conference)) === null || _r === void 0 ? void 0 : _r.getMetadata) === null || _s === void 0 ? void 0 : _s.call(_r));
            if (amIInBackstage(store.getState())) {
                _applyEntryMutePolicy(store);
            }
            break;
        }
        case CONFERENCE_WILL_LEAVE:
            _wasModeratorInPreviousRoom = isLocalParticipantModerator(store.getState());
            break;
        case CONFERENCE_LEFT:
            if (_metadataConference && _metadataUpdatedHandler) {
                _metadataConference.off(MHConferenceEvents.METADATA_UPDATED, _metadataUpdatedHandler);
            }
            if (_visitorsConference && _visitorsMessageHandler) {
                _visitorsConference.off(MHConferenceEvents.VISITORS_MESSAGE, _visitorsMessageHandler);
            }
            _metadataConference = undefined;
            _metadataUpdatedHandler = undefined;
            _visitorsConference = undefined;
            _visitorsMessageHandler = undefined;
            store.dispatch(setLocalRecordingAllowedState(undefined));
            store.dispatch(setMediaBlockAllState(undefined));
            store.dispatch(setBackstageRoster([]));
            store.dispatch(setMainRoomRoster([]));
            break;
        case PARTICIPANT_JOINED:
            if (!((_t = action.participant) === null || _t === void 0 ? void 0 : _t.local) && isLocalParticipantModerator(store.getState())) {
                store.dispatch(announceAudienceLayout());
            }
            break;
    }
    return result;
});
/**
 * Applies the scheduled meeting's "mute / force mute on entry" settings to the
 * local participant on arriving backstage. In the main room a moderator's
 * client applies them on PARTICIPANT_JOINED, but backstage is a separate room
 * that usually has no moderator in it, so the participant applies them itself.
 * The force-mute lock is keyed by the local id, which stays the same across a
 * room switch, so it carries on into the main room when they are brought live.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function _applyEntryMutePolicy(store) {
    var _a, _b;
    const state = store.getState();
    const confSettings = (_a = state['features/base/jwt']) === null || _a === void 0 ? void 0 : _a.confSettings;
    const localId = (_b = getLocalParticipant(state)) === null || _b === void 0 ? void 0 : _b.id;
    // A moderator who walked backstage may not have had their role re-granted
    // yet at CONFERENCE_JOINED, so their role in the room they left counts too.
    if (!confSettings || !localId || _wasModeratorInPreviousRoom || amIWebinarModerator(state)) {
        return;
    }
    const forceAudio = confSettings.FORCE_MUTE_PARTICIPANTS === 1;
    const forceVideo = confSettings.FORCE_VIDEO_MUTE_PARTICIPANTS === 1;
    if (forceAudio || confSettings.MUTE_PARTICIPANTS === 1) {
        store.dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
    }
    if (forceVideo || confSettings.VIDEO_MUTE_PARTICIPANTS === 1) {
        store.dispatch(muteLocal(true, MEDIA_TYPE.VIDEO));
    }
    if (forceAudio) {
        store.dispatch(addforcemuteRemoteParticipant(localId, MEDIA_TYPE.AUDIO, true));
    }
    if (forceVideo) {
        store.dispatch(addforcemuteRemoteParticipant(localId, MEDIA_TYPE.VIDEO, true));
    }
}
/**
 * Applies a prejoin "join hidden" choice to local state once the join lands, and
 * disarms the request.
 *
 * @param {IStore} store - The redux store.
 * @param {Object} conference - The conference that was just joined.
 * @returns {void}
 */
function _applyPrejoinJoinHidden(store, conference) {
    var _a;
    const audience = getJoinHiddenAudience(store.getState());
    if (!audience) {
        return;
    }
    const local = getLocalParticipant(store.getState());
    if (local === null || local === void 0 ? void 0 : local.id) {
        store.dispatch(participantUpdated({
            conference,
            id: local.id,
            local: true,
            webinarHidden: true,
            webinarHiddenVisibleTo: audience
        }));
    }
    else {
        logger.warn('join-hidden: no local participant at CONFERENCE_JOINED; '
            + 'the Room controls switch will read OFF until it is toggled');
    }
    store.dispatch(setJoinHidden(null));
    // The property is sticky on the conference, so a reconnect on this same
    // conference object would re-arm the request and undo an un-hide. An empty
    // value is what the server treats as "no request".
    (_a = conference === null || conference === void 0 ? void 0 : conference.setLocalParticipantProperty) === null || _a === void 0 ? void 0 : _a.call(conference, WEBINAR_HIDDEN_REQUEST_PROPERTY, '');
}
/**
 * Reads the local-recording policy out of room metadata and stores it in redux.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Object} metadata - The full room metadata object.
 * @returns {void}
 */
function _handleLocalRecordingMetadata(dispatch, metadata) {
    const policy = metadata === null || metadata === void 0 ? void 0 : metadata[LOCAL_RECORDING_METADATA_KEY];
    dispatch(setLocalRecordingAllowedState(policy && typeof policy.allowed === 'boolean' ? policy.allowed : undefined));
}
/**
 * Reads "block everyone's mic and camera" out of room metadata and stores it
 * in redux. The enforcement (self-mute, locked buttons) hangs off the derived
 * flag in the subscriber, so a moderator is never caught by it.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Object} metadata - The full room metadata object.
 * @returns {void}
 */
function _handleMediaBlockAllMetadata(dispatch, metadata) {
    const policy = metadata === null || metadata === void 0 ? void 0 : metadata[MEDIA_BLOCK_ALL_METADATA_KEY];
    dispatch(setMediaBlockAllState(policy && typeof policy.enabled === 'boolean' ? policy.enabled : undefined));
}
/**
 * Applies the moderator-selected visitor-facing layout from room metadata.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Function} getState - The redux getState function.
 * @param {Object} metadata - The full room metadata object.
 * @returns {void}
 */
function _handleAudienceLayoutMetadata(dispatch, getState, metadata) {
    const layout = metadata === null || metadata === void 0 ? void 0 : metadata[WEBINAR_AUDIENCE_LAYOUT_METADATA_KEY];
    const roomName = getMainRoomName(getState());
    if (!layout || typeof layout !== 'object' || !roomName) {
        return;
    }
    dispatch(setAudienceLayout(roomName, {
        showOnlyPanelists: Boolean(layout.showOnlyPanelists),
        stageOnlyMode: Boolean(layout.stageOnlyMode)
    }));
}
/**
 * Mutes the local participant when a moderator asked the server to, and the
 * server relayed the request here.
 *
 * A moderator who is backstage cannot mute anyone through Jicofo — it will not
 * take a mute request from outside the conference — so this is the only path
 * that exists for them. Mute only: an un-mute needs the target's own gesture,
 * and the server never sends one.
 *
 * targetId is required, unlike on the move request. That message is only ever
 * sent privately by Prosody; this name is ALSO what the moderator's client sends
 * as a groupchat to ask for the mute, so if Prosody is missing and the request
 * leaks through, matching on the target is what stops the whole room from muting
 * itself.
 *
 * @param {IStore} store - The redux store.
 * @param {Object} data - Message payload.
 * @param {boolean} senderIsModerator - Whether the sender was trusted.
 * @param {string} senderId - The sender id for diagnostics.
 * @returns {boolean}
 */
function _handleMuteRequest(store, data, senderIsModerator, senderId) {
    var _a;
    if ((data === null || data === void 0 ? void 0 : data.name) !== WEBINAR_MUTE_REQUEST) {
        return false;
    }
    const state = store.getState();
    if (!data.targetId || ((_a = getLocalParticipant(state)) === null || _a === void 0 ? void 0 : _a.id) !== data.targetId) {
        return true;
    }
    if (!senderIsModerator) {
        logger.warn(`Ignoring webinar mute request from ${senderId}`);
        return true;
    }
    store.dispatch(data.mediaType === 'video' ? setVideoMuted(true) : setAudioMuted(true));
    store.dispatch(showNotification({
        titleKey: 'webinar.mutedByModeratorTitle',
        descriptionKey: data.mediaType === 'video'
            ? 'webinar.mutedByModeratorVideoDescription'
            : 'webinar.mutedByModeratorAudioDescription'
    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    return true;
}
/**
 * Applies a trusted server-relayed move request when it is for this local stage
 * participant. If Prosody is missing and the original groupchat request leaks
 * through, targetId prevents every panelist in the room from moving.
 *
 * @param {IStore} store - The redux store.
 * @param {Object} data - Message payload.
 * @param {boolean} senderIsModerator - Whether the sender was trusted.
 * @param {string} senderId - The sender id for diagnostics.
 * @returns {boolean}
 */
function _handleMoveRequest(store, data, senderIsModerator, senderId) {
    if ((data === null || data === void 0 ? void 0 : data.name) !== WEBINAR_MOVE_REQUEST) {
        return false;
    }
    const state = store.getState();
    const localParticipant = getLocalParticipant(state);
    if (data.targetId && (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id) !== data.targetId) {
        return true;
    }
    // Only the sender is verified: a moderator may move ANY occupant backstage,
    // not just moderators and panelists, so the local role is not a condition.
    // Prosody re-checks the sender and issues the backstage invite that gets the
    // target through the room's door gate.
    if (!senderIsModerator) {
        logger.warn(`Ignoring webinar move request from ${senderId}`);
        return true;
    }
    store.dispatch(data.live ? goLive({ byModerator: true }) : goBackstage());
    return true;
}
/**
 * The notification for a room switch the server refused: what was attempted,
 * and -- when the server said so -- that a hold is the reason.
 *
 * @param {string} target - The room the switch was going to.
 * @param {boolean} heldBackstage - Whether the server refused because we are
 * held backstage.
 * @param {boolean} heldBreakout - Whether the server refused because we are
 * held in a breakout room.
 * @returns {Object} The notification props.
 */
function _describeRefusedSwitch(target, heldBackstage, heldBreakout) {
    if (heldBackstage) {
        return {
            titleKey: 'webinar.backstageHeldTitle',
            descriptionKey: 'webinar.heldDescription'
        };
    }
    if (heldBreakout) {
        return {
            titleKey: 'breakoutRooms.heldTitle',
            descriptionKey: 'breakoutRooms.heldDescription'
        };
    }
    let titleKey = 'webinar.mainJoinFailedTitle';
    if (target === 'backstage') {
        titleKey = 'webinar.backstageJoinFailedTitle';
    }
    else if (target === 'breakout') {
        titleKey = 'breakoutRooms.joinFailedTitle';
    }
    return {
        titleKey,
        descriptionKey: 'webinar.roomSwitchFailedDescription'
    };
}
/**
 * Joins the room a held guest belongs in after their initial main-room join
 * was refused: the backstage room of the meeting, or the breakout room named
 * in the refusal ({@code breakout-held:<room jid>}).
 *
 * @param {IStore} store - The redux store.
 * @param {Object} conference - The conference whose join was refused.
 * @param {boolean} heldBackstage - Whether the refusal was the backstage hold.
 * @param {string} errorText - The server's error text.
 * @returns {void}
 */
function _rejoinHeldRoom(store, conference, heldBackstage, errorText) {
    var _a, _b;
    if (heldBackstage) {
        const roomName = (_b = (_a = conference === null || conference === void 0 ? void 0 : conference.getName) === null || _a === void 0 ? void 0 : _a.call(conference)) !== null && _b !== void 0 ? _b : store.getState()['features/base/conference'].room;
        if (!roomName || isBackstageRoomName(roomName)) {
            return;
        }
        logger.info(`Held backstage: joining ${roomName}${BACKSTAGE_ROOM_SUFFIX} instead of the main room`);
        store.dispatch(showNotification({
            titleKey: 'webinar.backstageHeldTitle',
            descriptionKey: 'webinar.backstageHeldDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        store.dispatch(joinAdmittedRoom(`${roomName}${BACKSTAGE_ROOM_SUFFIX}`));
        return;
    }
    const roomJid = errorText.slice(BREAKOUT_HELD_ERROR.length + 1);
    const [node, domain] = roomJid.split('@');
    if (!node || !domain) {
        return;
    }
    // The way moveToRoom hands a breakout room to the conference: its node as
    // the name, its MUC domain attached, since it is not on the main MUC.
    // eslint-disable-next-line no-new-wrappers
    const target = new String(node);
    target.domain = domain;
    logger.info(`Held in breakout room ${roomJid}: joining it instead of the main room`);
    store.dispatch(showNotification({
        titleKey: 'breakoutRooms.heldTitle',
        descriptionKey: 'breakoutRooms.heldDescription'
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    store.dispatch(joinAdmittedRoom(target));
}
